import { useEffect, useMemo, useState } from 'react';
import { Activity, BarChart3, CircleDollarSign, WalletCards } from 'lucide-react';
import { fetchPowerBISemanticData } from '../services/powerbiService';

const WORKSPACE_ID = '7d441575-4aa9-4260-99dd-543f002c0340';
const DATASET_ID = '379012fc-fa5e-4178-8399-c518aef65120';
const TABLE_NAME = 'GrantUtylizationVision';
const TABLE_DISPLAY_NAME = 'Funds Utilization Data from VISION';
const REQUIRED_TABLE_COLUMNS = ['grant ref', 'expiry date'];

const normalizeColumn = (name) => {
  const raw = String(name || '').trim();
  const bracketMatch = raw.match(/\[([^\]]+)\]\s*$/);
  return String(bracketMatch ? bracketMatch[1] : raw).trim().toLowerCase();
};

const formatColumnHeader = (name) => {
  const raw = String(name || '').trim();
  const bracketMatch = raw.match(/\[([^\]]+)\]\s*$/);
  const columnName = bracketMatch ? bracketMatch[1] : raw;
  const normalizedColumn = normalizeColumn(columnName);
  if (normalizedColumn === 'expiry date') return 'Expiry';
  return columnName
    .replace(/almotment/gi, 'Allotment')
    .replace(/alotment/gi, 'Allotment')
    .replace(/utylization/gi, 'Utilization');
};

const findColumnKey = (columns, names) => (
  columns.find((column) => names.includes(normalizeColumn(column))) || null
);

const getVisibleColumns = (columns) => {
  const grantRefColumn = findColumnKey(columns, ['grant ref']);
  const displayableColumns = columns.filter((column) => {
    const normalizedColumn = normalizeColumn(column);
    return normalizedColumn !== 'allocation' && normalizedColumn !== 'expiry status';
  });
  const baseColumns = displayableColumns.slice(0, 8);
  const requiredColumns = REQUIRED_TABLE_COLUMNS
    .map((name) => findColumnKey(displayableColumns, [name]))
    .filter(Boolean)
    .filter((column) => !baseColumns.includes(column));

  return [grantRefColumn, ...baseColumns, ...requiredColumns]
    .filter(Boolean)
    .filter((column, idx, orderedColumns) => orderedColumns.indexOf(column) === idx);
};

const isCurrencyColumn = (column) => {
  const normalizedColumn = normalizeColumn(column);

  return !normalizedColumn.includes('%') &&
    !normalizedColumn.includes('percent') &&
    (
      normalizedColumn.includes('allotment') ||
      normalizedColumn.includes('almotment') ||
      normalizedColumn.includes('allocation') ||
      normalizedColumn.includes('amount') ||
      normalizedColumn.includes('commitment') ||
      normalizedColumn.includes('actual') ||
      normalizedColumn.includes('budget') ||
      normalizedColumn.includes('utilization') ||
      normalizedColumn.includes('utylization') ||
      normalizedColumn.includes('utilisation') ||
      normalizedColumn.includes('utilized') ||
      normalizedColumn.includes('spent')
    );
};

const isPercentColumn = (column) => {
  const normalizedColumn = normalizeColumn(column);
  return normalizedColumn.includes('%') || normalizedColumn.includes('percent');
};

const parseNumericValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;

  const numericValue = Number(value.replace(/[$,%\s,]/g, ''));
  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (typeof value === 'number') return Number.isInteger(value) ? value.toLocaleString() : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return String(value);
};

const formatUsdValue = (value) => {
  const numericValue = parseNumericValue(value);
  if (numericValue === null) return formatCellValue(value);

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(Math.ceil(numericValue));
};

const formatPercentValue = (value) => {
  const numericValue = parseNumericValue(value);
  if (numericValue === null) return formatCellValue(value);

  const percentValue = Math.abs(numericValue) <= 1 ? numericValue * 100 : numericValue;
  return `${percentValue.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
};

const formatTableCellValue = (value, column) => {
  if (isPercentColumn(column)) return formatPercentValue(value);
  if (isCurrencyColumn(column)) return formatUsdValue(value);
  return formatCellValue(value);
};

const getExpiryStatusClass = (status) => {
  const normalizedStatus = String(status || '').trim().toLowerCase();
  if (!normalizedStatus) return 'is-empty';
  if (normalizedStatus.includes('expired') || normalizedStatus.includes('overdue')) return 'is-expired';
  if (
    normalizedStatus.includes('expiring 0-3 months') ||
    normalizedStatus.includes('soon') ||
    normalizedStatus.includes('warning') ||
    normalizedStatus.includes('risk')
  ) return 'is-warning';
  if (normalizedStatus.includes('active') || normalizedStatus.includes('valid') || normalizedStatus.includes('current')) return 'is-active';
  return 'is-neutral';
};

const sumColumn = (rows, column) => rows.reduce((sum, row) => {
  const value = parseNumericValue(row?.[column]);
  return value === null ? sum : sum + value;
}, 0);

const averageColumn = (rows, column) => {
  const values = rows
    .map((row) => parseNumericValue(row?.[column]))
    .filter((value) => value !== null);

  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const ManagementKPI = () => {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadManagementKpis = async () => {
      setIsLoading(true);
      setError('');

      try {
        const data = await fetchPowerBISemanticData(
          DATASET_ID,
          WORKSPACE_ID,
          undefined,
          5000,
          TABLE_NAME
        );

        if (!mounted) return;
        setRows(Array.isArray(data?.data) ? data.data : []);
        setColumns(Array.isArray(data?.columns) ? data.columns : []);
      } catch (loadError) {
        if (!mounted) return;
        setRows([]);
        setColumns([]);
        setError(loadError?.message || 'Management KPI data is unavailable right now.');
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadManagementKpis();

    return () => {
      mounted = false;
    };
  }, []);

  const kpiCards = useMemo(() => {
    const visibleColumns = getVisibleColumns(columns);
    const percentColumn = visibleColumns.find(isPercentColumn) || null;
    const donorColumn = findColumnKey(columns, ['donor name', 'donor', 'funding source']);
    const allotmentColumn = findColumnKey(columns, ['allotment', 'almotment', 'total allotment', 'allocation', 'grant amount', 'budget']);
    const utilizationColumn = columns.find((column) => {
      const normalizedColumn = normalizeColumn(column);
      return column !== percentColumn &&
        !normalizedColumn.includes('%') &&
        !normalizedColumn.includes('percent') &&
        (
          normalizedColumn.includes('utilization') ||
          normalizedColumn.includes('utylization') ||
          normalizedColumn.includes('utilisation') ||
          normalizedColumn.includes('utilized') ||
          normalizedColumn.includes('spent')
        );
    });
    const donorCount = donorColumn
      ? new Set(rows.map((row) => String(row?.[donorColumn] || '').trim()).filter(Boolean)).size
      : rows.length;
    const totalAllotment = allotmentColumn ? sumColumn(rows, allotmentColumn) : null;
    const totalUtilization = utilizationColumn ? sumColumn(rows, utilizationColumn) : null;
    const averageUtilization = percentColumn ? averageColumn(rows, percentColumn) : null;

    return [
      {
        key: 'donor-count',
        icon: Activity,
        value: donorCount.toLocaleString(),
        label: 'Number of donors'
      },
      {
        key: 'total-allotment',
        icon: CircleDollarSign,
        value: totalAllotment === null ? 'N/A' : formatUsdValue(totalAllotment),
        label: 'Total Allotment'
      },
      {
        key: 'total-utilization',
        icon: WalletCards,
        value: totalUtilization === null ? 'N/A' : formatUsdValue(totalUtilization),
        label: 'Total Utilization'
      },
      {
        key: 'average-utilization',
        icon: BarChart3,
        value: averageUtilization === null ? 'N/A' : formatPercentValue(averageUtilization),
        label: 'Average Utilization %'
      }
    ];
  }, [columns, rows]);

  const visibleColumns = getVisibleColumns(columns);
  const visibleRows = rows.slice(0, 100);
  const expiryStatusColumn = findColumnKey(columns, ['expiry status']);

  return (
    <section className="home-section vaccines-page-section vaccines-dashboard">
      <div className="vaccines-dashboard-header">
        <div>
          <p className="vaccines-section-label">Management KPI</p>
          <h2 className="vaccines-dashboard-title">Management KPI dashboard</h2>
          <p className="vaccines-dashboard-subtitle">
            Review priority indicators for programme management, escalation, and performance follow-up.
          </p>
        </div>
      </div>

      <div className="vaccines-kpi-row management-kpi-row">
        {isLoading && [0, 1, 2, 3].map((idx) => (
          <article key={`loading-kpi-${idx}`} className="vaccines-kpi-card">
            <span className="vaccines-kpi-icon" aria-hidden="true">
              <Activity />
            </span>
            <div>
              <p className="vaccines-kpi-value">...</p>
              <p className="vaccines-kpi-label">Loading KPI</p>
            </div>
          </article>
        ))}

        {!isLoading && kpiCards.map((item) => {
          const Icon = item.icon;

          return (
            <article key={item.key} className={`vaccines-kpi-card vaccines-kpi-${item.key}`}>
              <span className="vaccines-kpi-icon" aria-hidden="true">
                <Icon />
              </span>
              <div>
                <p className="vaccines-kpi-value">{item.value}</p>
                <p className="vaccines-kpi-label">{item.label}</p>
              </div>
            </article>
          );
        })}

        {!isLoading && kpiCards.length === 0 && (
          <article className="vaccines-kpi-card">
            <span className="vaccines-kpi-icon" aria-hidden="true">
              <Activity size={18} />
            </span>
            <div>
              <p className="vaccines-kpi-value">N/A</p>
              <p className="vaccines-kpi-label">No KPI rows found</p>
            </div>
          </article>
        )}
      </div>

      <div className="vaccines-page-grid">
        <div className="vaccines-schedule-card">
          <div className="vaccines-card-header">
            <div>
              <h3 className="vaccines-card-title">{TABLE_DISPLAY_NAME}</h3>
            </div>
          </div>

          {error && <p className="placeholder-page-description">{error}</p>}

          <div className="vaccines-wuenic-table-wrap">
            <table className="vaccines-wuenic-table" aria-label="Management KPI semantic data">
              <thead>
                <tr>
                  {visibleColumns.map((column) => (
                    <th key={column} scope="col">{formatColumnHeader(column)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={Math.max(visibleColumns.length, 1)}>Loading Management KPI data...</td>
                  </tr>
                )}
                {!isLoading && !error && visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={Math.max(visibleColumns.length, 1)}>No rows returned from the semantic model.</td>
                  </tr>
                )}
                {!isLoading && !error && visibleRows.map((row, rowIdx) => (
                  <tr key={`management-kpi-row-${rowIdx}`}>
                    {visibleColumns.map((column) => {
                      const isExpiryColumn = normalizeColumn(column) === 'expiry date';
                      const expiryStatus = expiryStatusColumn ? row?.[expiryStatusColumn] : '';

                      return (
                        <td key={`${rowIdx}-${column}`}>
                          {isExpiryColumn ? (
                            <span className="management-expiry-cell">
                              <span>{formatCellValue(row?.[column])}</span>
                              {expiryStatus && (
                                <span className={`management-expiry-pill ${getExpiryStatusClass(expiryStatus)}`}>
                                  {formatCellValue(expiryStatus)}
                                </span>
                              )}
                            </span>
                          ) : (
                            formatTableCellValue(row?.[column], column)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ManagementKPI;
